import type { FileUpload, UploadedFile } from "../clients/Purchaser/FileUpload";

// CAD file types (use extensions because MIME types for CAD files are not consistently recognized)
export const _3D_GEOMETRY_FILE_TYPES = [".step", ".stp", ".iges", ".igs", ".brep", ".brp", ".stl", ".obj", ".ply"];
export const _2D_GEOMETRY_FILE_TYPES = [".dxf"];
export const TEXT_MIME_TYPES = [
    "text/plain",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];
export const PDF_MIME_TYPES = ["application/pdf"];
export const DOCUMENT_MIME_TYPES = [
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];
export const SPREADSHEET_MIME_TYPES = [
    "text/csv",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.oasis.opendocument.spreadsheet",
    "application/vnd.ms-excel.sheet.macroEnabled.12",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.template",
];
export const IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif", "image/tiff"];
export const ZIP_MIME_TYPES = ["application/zip", "application/x-zip-compressed", "multipart/x-zip"];
export const EMAIL_MIME_TYPES = ["message/rfc822", "application/vnd.ms-outlook"];

export const SUPPORTED_MIME_TYPES = [
    ...TEXT_MIME_TYPES,
    ...PDF_MIME_TYPES,
    ...SPREADSHEET_MIME_TYPES,
    ...IMAGE_MIME_TYPES,
    ...ZIP_MIME_TYPES,
    ...EMAIL_MIME_TYPES,
];

export function readFileToBase64String(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.onload = readEvent => {
            if (readEvent.target?.result) {
                resolve(readEvent.target.result.toString());
            } else {
                reject("Failed to read file.");
            }
        };

        fileReader.onerror = () => {
            reject("File reading error.");
        };

        fileReader.readAsDataURL(file);
    });
}

// Supports both the global File and PurchaserClient File (as UploadedFile) types
export function getFileSizeString(
    file:
        | {
              size: number;
          }
        | FileUpload,
): string {
    let fileSize = (file as { size: number }).size || (file as FileUpload).fileSizeBytes;

    // FileUpload fileSizeBytes can be null, in which case we'll return an empty string
    if (fileSize === undefined || fileSize === null) {
        return "";
    }
    const units = ["B", "KB", "MB", "GB", "TB"];

    // Divide the file size by 1024 until it's less than 1024 to get the correct units
    let unitIndex = 0;
    while (fileSize >= 1024 && unitIndex < units.length - 1) {
        fileSize /= 1024;
        unitIndex++;
    }

    // Return the file size with the correct units and 2 decimal places of precision
    return `${fileSize.toFixed(2)} ${units[unitIndex]}`;
}

// Converts a raw MIME type string to a human-readable label.
export function getMimeTypeString(mimeType: string | null | undefined): string {
    if (!mimeType) {
        return "Unknown Document Type";
    }
    // PDF
    if (mimeType === "application/pdf") {
        return "PDF Document";
    }
    // Images
    else if (mimeType.startsWith("image/")) {
        const imageType = mimeType.split("/")[1];
        return `${imageType.toUpperCase()} Image`;
    }
    // Excel
    else if (mimeType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
        return "Excel Spreadsheet";
    }
    // Older Excel format
    else if (mimeType === "application/vnd.ms-excel") {
        return "Excel Spreadsheet";
    }

    return "Unknown Document Type";
}

// Supports both the global File and PurchaserClient File (as FileUpload) types
export function getFileTypeString(file: File | FileUpload): string {
    // Check for 3D/2D model files first based on file extension (they might not have proper mime types)
    const filename = (file as File).name || (file as FileUpload).originalFilename;
    if (is3dGeometryFile(filename)) {
        return "3D Model";
    }
    if (is2dGeometryFile(filename)) {
        return "2D Model";
    }

    return getMimeTypeString((file as File).type || (file as FileUpload).mimeType);
}

export function getFilesFromInputEvent(e: React.ChangeEvent<HTMLInputElement>): File[] | undefined {
    if (!e.target.files) {
        return undefined;
    }
    return Array.from(e.target.files) as File[];
}

export function getSingleFileFromInputEvent(e: React.ChangeEvent<HTMLInputElement>): File | undefined {
    const files = getFilesFromInputEvent(e);
    if (files && files.length > 0) {
        return files[0];
    }
    return undefined;
}

/**
 * Reorders files so that non-image files come before image files. This is useful for displaying
 * files in a list where we want to prioritize documents over images.
 */
export const isImageFile = (mimeType: string) => mimeType.startsWith("image/");
export function documentsBeforeImages<T>(files: T[], mimeTypeOf: (file: T) => string): T[] {
    return [...files].sort((a, b) => Number(isImageFile(mimeTypeOf(a))) - Number(isImageFile(mimeTypeOf(b))));
}

// Outlook and Word name the graphics embedded in a signature image001.png and upward.
const EMBEDDED_IMAGE_FILENAME = /^image\d{3,}\.(png|jpe?g|gif|bmp)$/i;

// Above this an image is big enough to be a scan of a document rather than a logo.
const SIGNATURE_IMAGE_MAX_BYTES = 50_000;

/**
 * Determines whether a given file is likely an email signature image. We are just guessing here based on examples of prod data.
 *
 * GSTODO: Pull this out in favor of signature image recognition on the backend once message and file intents exist.
 */
export function isSignatureImage(file: { mimeType: string; originalFilename: string; fileSizeBytes: number | null }): boolean {
    if (!isImageFile(file.mimeType)) {
        return false;
    }
    return EMBEDDED_IMAGE_FILENAME.test(file.originalFilename) || (file.fileSizeBytes !== null && file.fileSizeBytes <= SIGNATURE_IMAGE_MAX_BYTES);
}

export function withoutSignatureImages<T extends { file: { mimeType: string; originalFilename: string; fileSizeBytes: number | null } }>(
    attachments: T[],
): T[] {
    return attachments.filter(attachment => !isSignatureImage(attachment.file));
}

// Bandaid: duplicate attachment rows are currently persisted per email, so the same
// file shows up multiple times. Dedup by original filename (keeping the first
// occurrence) wherever we render an email's attachments, until the save-side
// duplication is fixed. Generic over the attachment shape so the inbox and tabular
// quote views can share one implementation — delete this once the backend stops
// persisting duplicates.
export function dedupeAttachmentsByFilename<T extends { file: { originalFilename: string } }>(attachments: T[]): T[] {
    const seen = new Set<string>();
    return attachments.filter(attachment => {
        const filename = attachment.file.originalFilename;
        if (seen.has(filename)) {
            return false;
        }
        seen.add(filename);
        return true;
    });
}

// Hands a blob to the browser as a download under the name we choose. Revoking the object URL
// afterwards matters: without it the blob is pinned in memory for the life of the page, which for a
// spreadsheet export for example can be megabytes per click.
export function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

// Saves the file to disk rather than navigating to it, so the download keeps the
// original filename instead of the storage provider's internal one.
export async function downloadFileFromUrl(url: string, filename: string): Promise<void> {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
    }

    downloadBlob(await response.blob(), filename);
}

export function is3dGeometryFile(filename: string): boolean {
    const extension = (filename.split(".").pop() || "").toLowerCase();
    return _3D_GEOMETRY_FILE_TYPES.includes(`.${extension}`);
}

export function is2dGeometryFile(filename: string): boolean {
    const extension = (filename.split(".").pop() || "").toLowerCase();
    return _2D_GEOMETRY_FILE_TYPES.includes(`.${extension}`);
}
